import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { serviceSearchQuery } from '../../../../util/serviceSearch';
import { smoothScrollBehavior } from '../../../../util/prefersReducedMotion';
import { NamedLink } from '../../../../components';

import css from './HeroSpecialistsFeed.module.css';

const TITLE_ID = 'hero-specialists-title';
const TRACK_ID = 'hero-specialists-track';
// Card widths per band (--heroCardWidth in SectionHero.module.css, at most --heroCardMaxWidth 260px);
// the API variants are 300w and 600w.
const SIZES =
  '(max-width: 767px) 176px, (max-width: 1023px) 192px, (max-width: 1279px) 232px, 260px';
// The first cards are in the first screen: fetched at once, the first one with high priority.
const EAGER_CARDS = 3;

const isFocusVisible = el => {
  try {
    return el.matches(':focus-visible');
  } catch (e) {
    return true;
  }
};

// The /p/about blog arrow chevrons (AboutPage)
const ArrowIcon = ({ direction }) => (
  <svg className={css.arrowIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d={direction === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SpecialistCard = ({ specialist, index }) => {
  const { listingId, slug, name, place, online, category, experience, image } = specialist;
  const intl = useIntl();
  // D40: online-only listings and places outside Europe (and RU / UA / BY) read «Онлайн»
  const location = online ? intl.formatMessage({ id: 'SectionHero.specialists.online' }) : place;
  const isEager = index < EAGER_CARDS;
  return (
    <NamedLink name="ListingPage" params={{ id: listingId, slug }} className={css.card}>
      <img
        className={css.photo}
        src={image.src}
        srcSet={image.srcSet}
        sizes={SIZES}
        width={300}
        height={400}
        loading={isEager ? 'eager' : 'lazy'}
        fetchpriority={index === 0 ? 'high' : undefined}
        decoding="async"
        alt=""
      />
      <span className={css.panel}>
        <span className={css.nameRow}>
          <span className={css.name}>{name}</span>
          {location ? (
            <span className={css.place}>
              <span aria-hidden="true"> · </span>
              <span className={css.placeText}>{location}</span>
            </span>
          ) : null}
        </span>
        {category ? (
          <span className={css.category}>
            <span className={css.dot} aria-hidden="true" />
            <span className={css.categoryText}>{category}</span>
          </span>
        ) : null}
        {experience ? <span className={css.experience}>{experience}</span> : null}
      </span>
    </NamedLink>
  );
};

/**
 * D40: «Недавно добавили услуги» — a horizontal feed of specialist cards in the hero media slot
 * (in place of the photo). Native scroll with scroll-snap (works before hydration and on touch);
 * the arrow buttons are for pointer devices, disabled at the ends and hidden on touch screens.
 * Every card is one link to the specialist's newest service; the button at the end of the track
 * opens all services.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.specialists view models from selectHeroSpecialists
 * @param {Array<string>} props.serviceTypes service listing types (for the «Все услуги» end button)
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
const HeroSpecialistsFeed = props => {
  const { specialists, serviceTypes, className } = props;
  const intl = useIntl();
  const trackRef = useRef(null);
  const frameRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  // The arrow that takes keyboard focus after the focused one is disabled at a scroll edge (W3-9)
  const pendingFocusRef = useRef(null);
  // Before hydration both arrows are disabled; the track scrolls without them.
  const [edges, setEdges] = useState({ canPrev: false, canNext: false });

  const updateEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const maxScroll = track.scrollWidth - track.clientWidth;
    const canPrev = track.scrollLeft > 1;
    const canNext = track.scrollLeft < maxScroll - 1;
    // A disabled button drops its focus to <body>: a keyboard user on the arrow that reaches its edge
    // continues on the opposite arrow. Pointer focus (no :focus-visible) is left as it was.
    const active = document.activeElement;
    if (active === nextRef.current && !canNext && isFocusVisible(active)) {
      pendingFocusRef.current = prevRef;
    } else if (active === prevRef.current && !canPrev && isFocusVisible(active)) {
      pendingFocusRef.current = nextRef;
    }
    setEdges(prev =>
      prev.canPrev === canPrev && prev.canNext === canNext ? prev : { canPrev, canNext }
    );
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }
    const onScroll = () => {
      if (frameRef.current) {
        return;
      }
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        updateEdges();
      });
    };
    updateEdges();
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [updateEdges]);

  useEffect(() => {
    const target = pendingFocusRef.current?.current;
    pendingFocusRef.current = null;
    if (target && !target.disabled) {
      target.focus({ preventScroll: true });
    }
  }, [edges]);

  // One click moves by the cards that are fully visible (at least one card).
  const scrollByCards = direction => {
    const track = trackRef.current;
    const firstItem = track?.firstElementChild;
    if (!track || !firstItem) {
      return;
    }
    const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    const step = firstItem.getBoundingClientRect().width + gap;
    const cardsPerPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    track.scrollBy({
      left: direction * cardsPerPage * step,
      behavior: smoothScrollBehavior(),
    });
  };

  // Keyboard focus on a partly hidden card: browsers leave a partly visible element where it is, so
  // the card (and its focus ring) would stay cut. Scroll it to the start of the track (a snap point).
  // Keyboard only (:focus-visible): a pointer click must not move the card under the pointer.
  const handleFocus = e => {
    const track = trackRef.current;
    const item = e.target.closest('li');
    if (!track || !item || !isFocusVisible(e.target)) {
      return;
    }
    const padding = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    const trackBox = track.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    const isCut =
      itemBox.left < trackBox.left + padding || itemBox.right > trackBox.right - padding;
    if (isCut) {
      track.scrollTo({
        left: track.scrollLeft + itemBox.left - trackBox.left - padding,
        behavior: smoothScrollBehavior(),
      });
    }
  };

  return (
    <section className={classNames(css.root, className)} aria-labelledby={TITLE_ID}>
      <div className={css.header}>
        <h2 id={TITLE_ID} className={css.title}>
          <FormattedMessage id="SectionHero.specialists.title" />
        </h2>
      </div>
      {/* Both arrows come before the track in the DOM (keyboard order: prev, next, cards); CSS
          places them on the track's sides. */}
      <div className={css.carousel}>
        <button
          type="button"
          ref={prevRef}
          className={classNames(css.arrow, css.arrowPrev)}
          aria-label={intl.formatMessage({ id: 'SectionHero.specialists.prev' })}
          aria-controls={TRACK_ID}
          disabled={!edges.canPrev}
          onClick={() => scrollByCards(-1)}
        >
          <ArrowIcon direction="prev" />
        </button>
        <button
          type="button"
          ref={nextRef}
          className={classNames(css.arrow, css.arrowNext)}
          aria-label={intl.formatMessage({ id: 'SectionHero.specialists.next' })}
          aria-controls={TRACK_ID}
          disabled={!edges.canNext}
          onClick={() => scrollByCards(1)}
        >
          <ArrowIcon direction="next" />
        </button>
        <ul
          id={TRACK_ID}
          ref={trackRef}
          className={css.track}
          onFocus={handleFocus}
          aria-label={intl.formatMessage({ id: 'SectionHero.specialists.listLabel' })}
        >
          {specialists.map((specialist, index) => (
            <li key={specialist.listingId} className={css.item}>
              <SpecialistCard specialist={specialist} index={index} />
            </li>
          ))}
          <li className={css.endItem}>
            <NamedLink
              name="SearchPage"
              to={{ search: serviceSearchQuery(serviceTypes) }}
              className={classNames('delowo-button', 'delowo-button--sm', css.endButton)}
            >
              <FormattedMessage id="SectionHero.specialists.all" />
              <span aria-hidden="true">{'\u00a0→'}</span>
            </NamedLink>
          </li>
        </ul>
      </div>
    </section>
  );
};

// Placeholders while the feed loads on the client (SPA navigation to / with an empty store).
const SKELETON_CARDS = 4;

/**
 * D40 loading state: the feed's geometry (header row + 3:4 cards, same widths and radius) as neutral
 * surfaces, so the slot keeps its size and the real feed (or the photo) replaces it without a shift.
 * Decorative only (aria-hidden); the pulse runs only without prefers-reduced-motion.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const HeroSpecialistsFeedSkeleton = props => {
  const { className } = props;
  return (
    <div className={classNames(css.root, css.skeleton, className)} aria-hidden="true">
      <div className={css.header}>
        <span className={css.skeletonTitle} />
      </div>
      <ul className={classNames(css.track, css.skeletonTrack)}>
        {Array.from({ length: SKELETON_CARDS }, (_, i) => (
          <li key={i} className={css.item}>
            <span className={classNames(css.card, css.skeletonCard)} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default HeroSpecialistsFeed;
